import { ESCAPE_KEY } from '../constants/escape-key.constant';
import type { ClarityFlyInEscape } from '../models/clarity-fly-in-escape.model';
import { isFieldFocused } from '../utils/is-field-focused.util';

/** What a `document`-level Escape policy may do to the panel it guards. */
export interface ClarityFlyInEscapeTarget {
	/** The ref's live dismissal state — `setDismissible(false)` silences every policy. */
	readonly isDismissible: () => boolean;
	/**
	 * Closes the panel. `restoreFocus` is `true` only for brain `SidePanel`'s own handler, whose close
	 * path is the one that hands focus back (`SidePanel.tsx:198-216`).
	 */
	readonly dismiss: (restoreFocus: boolean) => void;
}

type EscapeListener = (event: KeyboardEvent) => void;

/** The `document` policy — `SidePanel.tsx:249-255`: every Escape, no other condition. */
function documentEscape(target: ClarityFlyInEscapeTarget): EscapeListener {
	return (event): void => {
		if (event.key === ESCAPE_KEY && target.isDismissible()) {
			target.dismiss(true);
		}
	};
}

/** The `document-unless-field` policy — `SkillDetailView.tsx:86-102`: bails on a handled event or a focused field, then marks it handled. */
function documentUnlessFieldEscape(document: Document, target: ClarityFlyInEscapeTarget): EscapeListener {
	return (event): void => {
		if (event.defaultPrevented || event.key !== ESCAPE_KEY) {
			return;
		}
		if (isFieldFocused(document) || !target.isDismissible()) {
			return;
		}

		target.dismiss(false);
		event.preventDefault();
	};
}

/** The `capture-unless-field` policy — `UserActivityFlyIn.tsx:70-82`: bails on a focused field, otherwise keeps the Escape to itself. */
function captureUnlessFieldEscape(document: Document, target: ClarityFlyInEscapeTarget): EscapeListener {
	return (event): void => {
		if (event.key !== ESCAPE_KEY || isFieldFocused(document) || !target.isDismissible()) {
			return;
		}

		event.stopImmediatePropagation();
		target.dismiss(false);
	};
}

function createEscapeListener(
	document: Document,
	policy: ClarityFlyInEscape,
	target: ClarityFlyInEscapeTarget,
): EscapeListener | undefined {
	switch (policy) {
		case 'document':
			return documentEscape(target);
		case 'document-unless-field':
			return documentUnlessFieldEscape(document, target);
		case 'capture-unless-field':
			return captureUnlessFieldEscape(document, target);
		case 'top-most':
		case 'none':
			return undefined;
	}
}

/** Ported from Clarity `apps/web/src/shared/ui/SidePanel.tsx` + `apps/web/src/features/systemDomain/components/SkillDetailView.tsx` + `apps/web/src/features/systemDomain/components/orgDetail/UserActivityFlyIn.tsx` (commit `f9217bb5a`). */
/**
 * Installs the `document` keydown listener an Escape policy stands for, and returns its removal.
 *
 * Returns `undefined` for the two policies that install none: `top-most` is the CDK dispatcher's and
 * the ref subscribes to it itself; `none` has no Escape at all. The listener sits on `document`, not
 * on the panel, because that is where brain's handlers sit — it is what lets one Escape close every
 * open panel (brain defect #133), and an Escape pressed inside a native modal dialog above them reach
 * the panels beneath it (brain defect #134).
 */
export function listenForDocumentEscape(
	document: Document,
	policy: ClarityFlyInEscape,
	target: ClarityFlyInEscapeTarget,
): (() => void) | undefined {
	const listener = createEscapeListener(document, policy, target);
	if (listener === undefined) {
		return undefined;
	}

	const capture = policy === 'capture-unless-field';
	document.addEventListener('keydown', listener, capture);

	return (): void => document.removeEventListener('keydown', listener, capture);
}
